<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { messageStamp, dynamicTime } from 'shared/helpers/timeHelper';

const props = defineProps({
  inboxName: {
    type: String,
    default: '',
  },
  inboxIcon: {
    type: String,
    default: '',
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  createdAt: {
    type: Number,
    default: 0,
  },
  updatedAt: {
    type: Number,
    default: 0,
  },
  templateName: {
    type: String,
    default: '',
  },
  metrics: {
    type: Object,
    default: null,
  },
  suppressionCount: {
    type: Number,
    default: 0,
  },
  isCompleted: {
    type: Boolean,
    default: false,
  },
});

const friendlyTemplateName = computed(() => {
  if (!props.templateName) return '';
  return props.templateName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
});

const deliveryRate = computed(() => {
  if (!props.metrics?.total) return null;
  const delivered = (props.metrics.delivered || 0) + (props.metrics.read || 0);
  return Math.round((delivered / props.metrics.total) * 100);
});
</script>

<template>
  <div class="flex items-center gap-2 overflow-hidden">
    <div class="flex items-center gap-1.5 flex-shrink-0">
      <Icon :icon="inboxIcon" class="flex-shrink-0 text-n-slate-12 size-3" />
      <span class="text-sm font-medium text-n-slate-12">
        {{ inboxName }}
      </span>
    </div>
    <span
      v-if="friendlyTemplateName"
      class="text-xs px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11 truncate flex-shrink"
    >
      {{ friendlyTemplateName }}
    </span>
    <span
      v-if="isCompleted && deliveryRate !== null"
      class="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-md flex-shrink-0"
      :class="
        deliveryRate >= 80
          ? 'bg-n-teal-3 text-n-teal-11'
          : deliveryRate >= 50
            ? 'bg-n-amber-3 text-n-amber-11'
            : 'bg-n-ruby-3 text-n-ruby-11'
      "
    >
      <span class="i-lucide-bar-chart-2 text-xs" />
      {{ `${deliveryRate}%` }}
    </span>
    <span
      v-if="suppressionCount > 0"
      class="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-md flex-shrink-0 bg-n-amber-3 text-n-amber-11"
    >
      <span class="i-lucide-shield-ban text-xs" />
      {{ suppressionCount }}
    </span>
    <span
      v-if="createdAt"
      class="flex-shrink-0 text-xs text-n-slate-11 flex items-center gap-1"
    >
      <span class="i-lucide-calendar size-3" />
      {{ messageStamp(createdAt, 'LLL d, h:mm a') }}
    </span>
    <span
      v-if="!isCompleted && scheduledAt"
      class="flex-shrink-0 text-xs text-n-slate-11 flex items-center gap-1"
      :title="messageStamp(scheduledAt, 'LLL d, h:mm a')"
    >
      <span class="i-lucide-clock size-3" />
      {{ dynamicTime(scheduledAt) }}
    </span>
    <span
      v-else-if="isCompleted && updatedAt"
      class="flex-shrink-0 text-xs text-n-slate-11 flex items-center gap-1"
    >
      <span class="i-lucide-check-circle size-3" />
      {{ messageStamp(updatedAt, 'LLL d, h:mm a') }}
    </span>
  </div>
</template>
